/**
 * Amount Discount Calculation Utilities
 * Handles quantity-based discounts with CUMULATIVE logic by paritMerakez
 * Discount tiers are computed across ALL items with the same paritMerakez
 */

// ============================================
// Basic Helpers
// ============================================

/**
 * Sum total qty across all items with a specific paritMerakez
 * @param {Object} params
 * @param {string} params.paritMerakez - The paritMerakez to sum for
 * @param {Object} params.cartLinesByMakat - Cart lines map (makat -> { qty })
 * @param {Object} params.itemsByMakat - Catalog items map (makat -> item with paritMerakez)
 * @returns {number} Total quantity
 */
export function sumQtyByParitMerakez({ paritMerakez, cartLinesByMakat, itemsByMakat }) {
  if (!paritMerakez) return 0

  let total = 0
  const targetMerakez = String(paritMerakez)

  for (const [makat, lineData] of Object.entries(cartLinesByMakat)) {
    const item = itemsByMakat.get ? itemsByMakat.get(makat) : itemsByMakat[makat]
    if (!item) continue

    const itemMerakez = String(item.paritMerakez || '')
    if (itemMerakez === targetMerakez) {
      total += lineData.qty || 0
    }
  }

  return total
}

/**
 * Get all makats in cart that share a paritMerakez
 * @param {Object} params
 * @param {string} params.paritMerakez
 * @param {Object} params.cartLinesByMakat
 * @param {Object} params.itemsByMakat
 * @returns {string[]} Array of makats
 */
export function getMakatsForParitMerakez({ paritMerakez, cartLinesByMakat, itemsByMakat }) {
  if (!paritMerakez) return []

  const makats = []
  const targetMerakez = String(paritMerakez)

  for (const [makat, lineData] of Object.entries(cartLinesByMakat)) {
    if (!lineData.qty || lineData.qty <= 0) continue

    const item = itemsByMakat.get ? itemsByMakat.get(makat) : itemsByMakat[makat]
    if (!item) continue

    const itemMerakez = String(item.paritMerakez || '')
    if (itemMerakez === targetMerakez) {
      makats.push(makat)
    }
  }

  return makats
}

// ============================================
// Tier Selection
// ============================================

/**
 * Pick the applicable discount tier for a given total qty
 * Returns the tier with the HIGHEST minQty that is <= totalQty
 * @param {Array} discounts - Array of { minQty, discountPercent }
 * @param {number} totalQty - Total cumulative qty
 * @returns {{ minQty: number, discountPercent: number } | null}
 */
export function pickDiscountTierForQty(discounts, totalQty) {
  if (!Array.isArray(discounts) || discounts.length === 0) return null
  if (totalQty <= 0) return null

  // Sort by minQty ascending
  const sorted = [...discounts].sort((a, b) => a.minQty - b.minQty)

  let matchedTier = null
  for (const tier of sorted) {
    if (totalQty >= tier.minQty) {
      matchedTier = tier
    } else {
      break
    }
  }

  return matchedTier
}

/**
 * Pick the tier that is GUARANTEED by the first possible add, when no tier
 * applies yet. Quantity can only grow in multiples of minAdd (one box for
 * box-only items), so if totalQty + minAdd reaches a tier, the base price is
 * unpurchasable — every real order lands on that tier or better. The UI then
 * presents the tier price up front instead of a base price nobody can pay
 * (same principle as the madregot "anticipated step").
 * Callers add their own context gate (e.g. item not in cart).
 * @param {Object} params
 * @param {Array} params.discounts - Array of { minQty, discountPercent }
 * @param {number} params.totalQty - Current cumulative qty for the group
 * @param {number} params.minAdd - Minimum possible add (boxSize or 1)
 * @returns {{ minQty: number, discountPercent: number } | null}
 */
export function pickAnticipatedDiscountTier({ discounts, totalQty, minAdd }) {
  if (!Array.isArray(discounts) || discounts.length === 0) return null
  const add = Number(minAdd)
  if (!(add > 0)) return null
  const qty = Math.max(0, Number(totalQty) || 0)
  // A tier already applies — nothing to anticipate
  if (pickDiscountTierForQty(discounts, qty)) return null
  return pickDiscountTierForQty(discounts, qty + add)
}

/**
 * Calculate unit price after discount
 * @param {number} basePrice - Base unit price (pre-discount)
 * @param {number} discountPercent - Discount percentage (e.g., 5 for 5%)
 * @returns {number} Price after discount
 */
export function applyDiscountPercent(basePrice, discountPercent) {
  if (isNaN(basePrice) || basePrice <= 0) return 0
  if (isNaN(discountPercent) || discountPercent <= 0) return basePrice

  const multiplier = 1 - discountPercent / 100
  return Math.max(0, basePrice * multiplier)
}

// ============================================
// Main Engine Function
// ============================================

/**
 * Compute cumulative amount discount application for an item
 * @param {Object} params
 * @param {string} params.paritMerakez - The paritMerakez for this item
 * @param {number} params.basePrice - Item's catalog price (pre-VAT, pre-deposit)
 * @param {Array} params.discounts - Array of { minQty, discountPercent }
 * @param {Object} params.cartLinesByMakat - Cart lines map (makat -> { qty })
 * @param {Object} params.itemsByMakat - Catalog items map (makat -> item)
 * @returns {Object} Application result
 */
export function computeCumulativeAmountDiscountApplication({
  paritMerakez,
  basePrice,
  discounts,
  cartLinesByMakat,
  itemsByMakat,
}) {
  // Calculate cumulative qty across all items with same paritMerakez
  const totalQty = sumQtyByParitMerakez({
    paritMerakez,
    cartLinesByMakat,
    itemsByMakat,
  })

  // No qty in cart - no discount applies
  if (totalQty <= 0) {
    return {
      paritMerakez,
      totalQty: 0,
      appliedTier: null,
      effectiveUnitPrice: basePrice,
      isDiscounted: false,
      discountPercent: 0,
      savings: 0,
    }
  }

  // Pick applicable tier
  const appliedTier = pickDiscountTierForQty(discounts, totalQty)

  // No tier applies
  if (!appliedTier) {
    return {
      paritMerakez,
      totalQty,
      appliedTier: null,
      effectiveUnitPrice: basePrice,
      isDiscounted: false,
      discountPercent: 0,
      savings: 0,
    }
  }

  // Calculate discounted price
  const effectiveUnitPrice = applyDiscountPercent(basePrice, appliedTier.discountPercent)
  const savings = basePrice - effectiveUnitPrice

  return {
    paritMerakez,
    totalQty,
    appliedTier: {
      minQty: appliedTier.minQty,
      discountPercent: appliedTier.discountPercent,
    },
    effectiveUnitPrice,
    isDiscounted: true,
    discountPercent: appliedTier.discountPercent,
    savings,
  }
}

// ============================================
// Next Tier Hint
// ============================================

/**
 * Get hint for next discount tier
 * @param {Array} discounts - Array of { minQty, discountPercent }
 * @param {number} totalQty - Current cumulative qty
 * @returns {{ nextTier: Object, unitsNeeded: number } | null}
 */
export function getNextDiscountTierHint(discounts, totalQty) {
  if (!Array.isArray(discounts) || discounts.length === 0) return null
  if (totalQty <= 0) return null

  // Sort by minQty ascending
  const sorted = [...discounts].sort((a, b) => a.minQty - b.minQty)

  // Find next tier (first tier where minQty > totalQty)
  for (const tier of sorted) {
    if (tier.minQty > totalQty) {
      return {
        nextTier: tier,
        unitsNeeded: tier.minQty - totalQty,
      }
    }
  }

  return null // Already at highest tier
}

// ============================================
// Discount Ladder Builder
// ============================================

/**
 * Build discount ladder with active tier highlighted
 * Includes base price as tier 0
 * @param {Object} params
 * @param {number} params.baseUnitPrice - Item price (pre-VAT, pre-deposit)
 * @param {number} params.totalQty - Cumulative qty for paritMerakez
 * @param {Array} params.discounts - Array of { minQty, discountPercent }
 * @returns {Array} Ladder array with { minQty, discountPercent, unitPriceAfterDiscount, isActive, isBase }
 */
export function buildDiscountLadderWithBase({ baseUnitPrice, totalQty, discounts }) {
  if (!Array.isArray(discounts) || discounts.length === 0) {
    return []
  }

  const price = Number(baseUnitPrice)
  if (isNaN(price) || price <= 0) {
    return []
  }

  const qty = Number(totalQty) || 0

  // Sort discounts by minQty
  const sorted = [...discounts].sort((a, b) => a.minQty - b.minQty)

  // Find active tier: largest minQty <= qty
  let activeTierMinQty = null
  for (const tier of sorted) {
    if (qty >= tier.minQty) {
      activeTierMinQty = tier.minQty
    }
  }

  // Build ladder with base price as first tier
  const ladder = []

  // Base price tier (qty = 0 or below first discount tier)
  const isBaseActive = qty === 0 || (sorted.length > 0 && qty < sorted[0].minQty)
  ladder.push({
    minQty: 0,
    discountPercent: 0,
    unitPriceAfterDiscount: price.toFixed(2),
    isActive: isBaseActive,
    isBase: true,
  })

  // Discount tiers
  for (const tier of sorted) {
    const unitPriceAfterDiscount = applyDiscountPercent(price, tier.discountPercent)

    ladder.push({
      minQty: tier.minQty,
      discountPercent: tier.discountPercent,
      unitPriceAfterDiscount: unitPriceAfterDiscount.toFixed(2),
      isActive: tier.minQty === activeTierMinQty,
      isBase: false,
    })
  }

  return ladder
}

// ============================================
// Multi-Item Group Detection
// ============================================

/**
 * Get all makats in catalog that share a paritMerakez
 * @param {string} paritMerakez - The paritMerakez to search for
 * @param {Map} itemsByMakat - Catalog items map (makat -> item)
 * @returns {string[]} Array of makats
 */
export function getAllMakatsWithParitMerakez(paritMerakez, itemsByMakat) {
  if (!paritMerakez) return []

  const makats = []
  const targetMerakez = String(paritMerakez)

  for (const [makat, item] of itemsByMakat) {
    const itemMerakez = String(item.paritMerakez || '')
    if (itemMerakez === targetMerakez) {
      makats.push(makat)
    }
  }

  return makats
}
